import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  ProductSelection,
  ScheduleMemberChange,
  UpdateScheduleMemberRequest,
} from '@nucleus-commerce/api-client/admin';

import { schedulesApi } from '../api/schedules-api';

import { refreshSchedule } from './invalidate';
import { scheduleKeys } from './schedule-keys';

/**
 * Adds products to the pool in one call: ticked ids (at most 5,000, the member cap) or a
 * selection of every matching product the server resolves. Each one gets a draft store product
 * in every target store at once. A product another schedule owns in a target store comes back
 * as a 422 SCHEDULE_CONFLICT item and is skipped, products already in the pool are no-ops; a
 * selection's answer lists the failures only, with `matched`.
 */
export function useAddScheduleMembers(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ selection, pinned }: { selection: ProductSelection; pinned: boolean }) =>
      schedulesApi.addMembers(
        id,
        selection.mode === 'ids' ? { productIds: selection.ids, pinned } : { selection, pinned },
      ),
    meta: { silentError: true },
    onSettled: () =>
      Promise.all([
        refreshSchedule(queryClient, id),
        queryClient.invalidateQueries({ queryKey: scheduleKeys.members(id) }),
      ]),
  });
}

/** Pin, exclude or reorder one member; pinning clears excluded and the other way round. */
export function useUpdateScheduleMember(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, body }: { productId: string; body: UpdateScheduleMemberRequest }) =>
      schedulesApi.updateMember(id, productId, body),
    meta: { silentError: true },
    onSuccess: (_change: ScheduleMemberChange) =>
      Promise.all([
        refreshSchedule(queryClient, id),
        queryClient.invalidateQueries({ queryKey: scheduleKeys.members(id) }),
        queryClient.invalidateQueries({ queryKey: scheduleKeys.runs(id) }),
      ]),
  });
}

/**
 * Takes a product out of the pool; its store products stay, a live one is unpublished. A rule
 * member is excluded instead (`excluded: true`); the dialog says which happened.
 */
export function useRemoveScheduleMember(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) => schedulesApi.removeMember(id, productId),
    meta: { silentError: true },
    onSuccess: () =>
      Promise.all([
        refreshSchedule(queryClient, id),
        queryClient.invalidateQueries({ queryKey: scheduleKeys.members(id) }),
        queryClient.invalidateQueries({ queryKey: scheduleKeys.runs(id) }),
      ]),
  });
}
